import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'primereact/checkbox';
import { ChecklistTask as ChecklistTaskType } from '../../data/checklistData';
import ColorTag, { ColorTagColor } from '../../../../shared/components/color-tag/ColorTag';
import PlaybookTaskDialog from './PlaybookTaskDialog';

const IMPACT_COLOR: Record<NonNullable<ChecklistTaskType['impact']>, ColorTagColor> = {
  high: 'red',
  medium: 'orange',
  low: 'green',
};

interface PlaybookTaskProps {
  task: ChecklistTaskType;
  isChecked: boolean;
  onToggle: () => void;
}

/** Task row (Key-Takeaway item style): checkbox + title/description + impact tag.
 *  The whole row toggles completion; the trailing button (only while not completed)
 *  opens the instruction popup. When completed, a translucent green overlay covers
 *  the row and clicking it un-checks the task. */
const PlaybookTask: React.FC<PlaybookTaskProps> = ({ task, isChecked, onToggle }) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Presence follows the catalog (the canon); the i18n bundle only translates it.
  const instructionKey = task.titleKey.replace(/\.title$/, '.instruction');
  const hasInstruction = !!task.defaultInstruction;
  const instructionText = hasInstruction ? t(instructionKey, { defaultValue: task.defaultInstruction }) : '';

  // Toggle on row click, but not when the click originates from the details button.
  const handleRowToggle = (target: EventTarget) => {
    if ((target as HTMLElement).closest?.('.playbook-task__details')) return;
    onToggle();
  };

  return (
    <>
      <div
        className={`playbook-task${isChecked ? ' playbook-task--checked' : ''}`}
        role="button"
        tabIndex={0}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => handleRowToggle(e.target)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
      <div className="playbook-task__checkbox">
        <Checkbox checked={isChecked} tabIndex={-1} />
      </div>

      <div className="playbook-task__body">
        <div className="playbook-task__title">{t(task.titleKey, { defaultValue: task.defaultTitle })}</div>
        {task.descriptionKey && (
          <p className="playbook-task__desc">
            {t(task.descriptionKey, { defaultValue: task.defaultDescription })}
          </p>
        )}
      </div>

      {task.impact && (
        <ColorTag color={IMPACT_COLOR[task.impact]}>
          {t(`geoChecklist.pb.impact.${task.impact}`)}
        </ColorTag>
      )}

      {hasInstruction && !isChecked && (
        <button
          type="button"
          className="playbook-task__details"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            setDialogOpen(true);
          }}
          title={t('geoChecklist.learnMore')}
          aria-label={t('geoChecklist.learnMore')}
        >
          <span className="ico--right ico--xs" />
        </button>
      )}

      {isChecked && (
        <div className="playbook-task__overlay">
          <span className="playbook-completed-tag">
            <span className="ico--tick ico--sm playbook-completed-tag__icon" aria-hidden="true" />
            {t('geoChecklist.completed')}
          </span>
        </div>
      )}
      </div>

      {/* Rendered as a sibling (not inside the row) so its portal clicks don't
          bubble through React's tree to the row's toggle handler. */}
      {hasInstruction && !isChecked && (
        <PlaybookTaskDialog
          visible={dialogOpen}
          onHide={() => setDialogOpen(false)}
          task={task}
          instructionText={instructionText}
        />
      )}
    </>
  );
};

export default PlaybookTask;
